import { AppPrices, FrequencyPrice, PriceDisplayOverride, UnitBillingFrequency } from '@vendasta/marketplace-apps';
import { ItemBasePrice } from './base';
import { ItemFrequencyPrice, calculateUnitPriceFromInstancePrice, getFrequencyPrices } from './frequency';
import { getPartnerCurrency, toGalaxyFrequency } from '@vendasta/billing-ui';
import { InventoryItemWithPricing } from '../inventory-item';
import { revenuePeriodToGalaxyFrequency } from '../../billing-term-dialog/sales-to-galaxy-frequency.pipe';
import { RevenuePeriod } from '@vendasta/sales-orders';

export type ItemRetailPrice = ItemBasePrice & ItemFrequencyPrice;

export type ParseRetailPriceConfig = {
  appPrices: AppPrices;
  item: InventoryItemWithPricing;
  marketCurrency: string;
};

export function parseRetailItemPrice({ appPrices, item, marketCurrency }: ParseRetailPriceConfig): ItemRetailPrice {
  const editionId = item.type === 'app' ? item.edition?.editionId : undefined;
  const appPrice = appPrices?.pricesForContexts?.['retail'];
  const unitPrice = appPrice?.pricesForEditions?.[editionId ?? ''];
  let currency = getPartnerCurrency(appPrice);

  let frequencyPrices = getFrequencyPrices(appPrices, editionId, 'retail');
  if (!frequencyPrices?.length) {
    // fallback to wholesale pricing if retail is unavailable,
    frequencyPrices = getFrequencyPrices(appPrices, editionId, 'wholesale');
    currency = marketCurrency;
  }

  // retail price is limited to one frequency
  const retailPrice = parseRetailFrequencyPrice({
    mpFrequencyPrice: frequencyPrices[0],
    item,
  });

  return {
    ...retailPrice,
    currency: currency,
    contactSales: appPrice?.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES,
    discounts: unitPrice?.discount ?? [],
  };
}

type parseRetailFrequencyPriceConfig = {
  mpFrequencyPrice: FrequencyPrice;
  item: InventoryItemWithPricing;
};

function parseRetailFrequencyPrice({
  mpFrequencyPrice,
  item: item,
}: parseRetailFrequencyPriceConfig): ItemFrequencyPrice {
  const minimumPrice = mpFrequencyPrice?.pricingRules?.[0].price || 0;
  let price = minimumPrice;
  let frequency: ItemFrequencyPrice['frequency'] = toGalaxyFrequency(
    mpFrequencyPrice?.frequency ?? UnitBillingFrequency.UNIT_BILLING_FREQUENCY_ONETIME,
  );

  const hasInstancePrice = Boolean(mpFrequencyPrice?.instancePrice);
  if (hasInstancePrice) {
    price = calculateUnitPriceFromInstancePrice(
      mpFrequencyPrice.instancePrice,
      mpFrequencyPrice.setupFee,
      item.quantity,
    );
  }

  const revenueComponent = item?.pricing?.currentRevenue?.revenueComponents?.[0];
  if (revenueComponent) {
    frequency = revenuePeriodToGalaxyFrequency(revenueComponent.period ?? RevenuePeriod.ONETIME);
    price = revenueComponent.value ?? 0;
  }

  return {
    price: price,
    total: price * item.quantity,
    frequency: frequency,
    minimumPrice: minimumPrice,
  };
}
